import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Sparkles, PlayCircle, RefreshCw, FileText, AlertCircle,
  ChevronLeft, ChevronRight, ScrollText, AlertTriangle, Activity as ActivityIcon,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import MrvKpiCard from '../../components/mrv/MrvKpiCard';

/**
 * M4 — Audit & Integrity
 *
 * Reads SAARV's tenant.audit_logs filtered for MRV resources, plus integrity
 * scans on mrv_activity_data + ledger transactions. Mirrors the saarv-mrv-beta
 * Audit Dashboard layout: 4 KPI cards, audit-activity bar chart, by-action
 * type panel, user activity panel.
 */

const SUB_TABS = [
  { id: 'dashboard', label: 'Audit Dashboard', Icon: ShieldCheck },
  { id: 'logs',      label: 'Audit Logs',      Icon: ScrollText },
  { id: 'integrity', label: 'Integrity Checks', Icon: AlertTriangle },
];

const ACTION_COLORS = {
  'mrv.activity.create':       'bg-emerald-50 text-emerald-700',
  'mrv.activity.delete':       'bg-red-50 text-red-700',
  'mrv.ingest':                'bg-blue-50 text-blue-700',
  'mrv.standardisation.apply': 'bg-violet-50 text-violet-700',
  'mrv.emissions.calculate':   'bg-orange-50 text-orange-700',
  'mrv.report.create':         'bg-amber-50 text-amber-700',
  'mrv.registry.submit':       'bg-cyan-50 text-cyan-700',
  'mrv.credit.issue':          'bg-emerald-50 text-emerald-700',
  'mrv.integrity.check':       'bg-violet-50 text-violet-700',
  'mrv.asset.create':          'bg-gray-100 text-gray-700',
  'mrv.asset.delete':          'bg-gray-100 text-gray-700',
};

export default function AuditPanel({ siteId }) {
  const { showSuccess, showError, showInfo } = useAlert();
  const [activeSubTab, setActiveSubTab] = useState('dashboard');
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [integrityResult, setIntegrityResult] = useState(null);
  const PAGE_SIZE = 50;

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `days=30${siteId ? `&site_id=${siteId}` : ''}`;
      const res = await axios.get(`${API}/mrv/audit/summary?${qs}`);
      setSummary(res.data);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load audit summary');
    } finally {
      setLoading(false);
    }
  }, [showError, siteId]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(
        `${API}/mrv/audit/logs?offset=${logsPage * PAGE_SIZE}&limit=${PAGE_SIZE}`
      );
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLogsLoading(false);
    }
  }, [logsPage, showError]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => {
    if (activeSubTab === 'logs') fetchLogs();
  }, [activeSubTab, fetchLogs]);

  const runIntegrityCheck = async () => {
    showInfo('Running integrity check…');
    try {
      const res = await axios.post(`${API}/mrv/audit/integrity-check`);
      setIntegrityResult(res.data);
      showSuccess(`Integrity check complete — ${res.data.status}`);
      fetchSummary();
    } catch (e) {
      showError(e.response?.data?.detail || 'Integrity check failed');
    }
  };

  const generateAiReport = async () => {
    showInfo('Generating AI integrity report…');
    try {
      const res = await axios.post(`${API}/mrv/audit/ai-integrity-report`);
      setAiResult(res.data);
    } catch (e) {
      showError(e.response?.data?.detail || 'AI report failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil(logsTotal / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Data Integrity & Audit</h2>
          <div className="text-xs text-gray-500">M4 — Immutable audit trail, integrity verification, anomaly detection</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={generateAiReport}
            className="px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1"
          >
            <Sparkles className="w-4 h-4" /> AI Integrity Report
          </button>
          <button
            onClick={runIntegrityCheck}
            className="px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-medium inline-flex items-center gap-1"
          >
            <PlayCircle className="w-4 h-4" /> Run Integrity Check
          </button>
          <button onClick={fetchSummary} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex gap-2">
          {SUB_TABS.map(({ id, label, Icon }) => (
            <button
              key={id} type="button" onClick={() => setActiveSubTab(id)}
              className={`py-2 px-3 inline-flex items-center gap-1 border-b-2 text-sm font-medium ${
                activeSubTab === id
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </div>

      {/* AI report */}
      {aiResult && (
        <div className="mb-4 p-3 border border-violet-200 bg-violet-50 rounded-md">
          <div className="font-semibold text-violet-900 mb-1 flex items-center gap-1">
            <Sparkles className="w-4 h-4" />
            {aiResult.headline || 'AI integrity assessment'}
            {aiResult.integrity_grade && (
              <span className="ml-2 px-2 py-0.5 bg-white text-xs font-mono rounded">
                Grade: {aiResult.integrity_grade}
              </span>
            )}
          </div>
          {aiResult.key_observations?.length > 0 && (
            <div className="text-sm">
              <strong>Key observations:</strong>
              <ul className="list-disc list-inside ml-2 text-gray-700">
                {aiResult.key_observations.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
          {aiResult.recommended_actions?.length > 0 && (
            <div className="text-sm mt-2">
              <strong>Recommended actions:</strong>
              <ul className="list-disc list-inside ml-2 text-gray-700">
                {aiResult.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          <button
            onClick={() => setAiResult(null)}
            className="mt-2 text-xs text-violet-700 hover:text-violet-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Sub-tab content */}
      {activeSubTab === 'dashboard' && <AuditDashboard summary={summary} loading={loading} />}
      {activeSubTab === 'logs' && (
        <AuditLogsTable
          logs={logs}
          total={logsTotal}
          page={logsPage}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          loading={logsLoading}
          onPage={setLogsPage}
        />
      )}
      {activeSubTab === 'integrity' && (
        <IntegrityView summary={summary} result={integrityResult} onRun={runIntegrityCheck} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function AuditDashboard({ summary, loading }) {
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MrvKpiCard
          title="Audit Logs"
          value={summary?.audit_logs_count != null ? Number(summary.audit_logs_count).toLocaleString() : '—'}
          sub="Total audit entries (last 30d)"
          Icon={ScrollText}
          accent="blue"
          loading={loading}
        />
        <MrvKpiCard
          title="Integrity"
          value={summary?.integrity_percent != null ? `${summary.integrity_percent}%` : '—'}
          sub={`${summary?.rows_hashed || 0} of ${summary?.rows_total || 0} hashed`}
          Icon={ShieldCheck}
          accent="emerald"
          loading={loading}
        />
        <MrvKpiCard
          title="Duplicates"
          value={summary?.duplicates != null ? Number(summary.duplicates).toLocaleString() : '—'}
          sub="Potential duplicate records"
          Icon={AlertCircle}
          accent="amber"
          loading={loading}
        />
        <MrvKpiCard
          title="Outliers"
          value={summary?.outliers != null ? Number(summary.outliers).toLocaleString() : '—'}
          sub="Values >3σ from per-type mean"
          Icon={AlertTriangle}
          accent="red"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Audit activity */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Audit Activity</h3>
            <span className="text-xs text-gray-500">Daily volume — last 30 days</span>
          </div>
          {(summary?.daily_activity || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={summary.daily_activity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip labelFormatter={v => v ? new Date(v).toLocaleDateString() : ''} />
                <Bar dataKey="log_count" fill="#3b82f6" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart hint="No audit entries yet — interact with MRV to populate" />
          )}
        </div>

        {/* By Action Type */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">By Action Type</h3>
          {(summary?.by_action_type || []).length > 0 ? (
            <div className="space-y-2">
              {summary.by_action_type.map(a => (
                <div key={a.action_type} className="flex items-center justify-between text-xs">
                  <span className={`px-2 py-0.5 rounded font-medium ${ACTION_COLORS[a.action_type] || 'bg-gray-100 text-gray-700'}`}>
                    {a.action_type}
                  </span>
                  <span className="font-mono font-semibold text-gray-900">{a.log_count}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyChart hint="No actions yet" compact />
          )}
        </div>
      </div>

      {/* User activity */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">User Activity</h3>
        {(summary?.by_actor || []).length > 0 ? (
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500 text-xs">
              <tr>
                <th className="py-1">Actor</th>
                <th className="py-1 text-right">Audit entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.by_actor.map(a => (
                <tr key={a.actor}>
                  <td className="py-1.5 text-gray-700">{a.actor}</td>
                  <td className="py-1.5 text-right font-mono">{a.log_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyChart hint="No user activity in window" compact />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AuditLogsTable({ logs, total, page, totalPages, pageSize, loading, onPage }) {
  const offset = page * pageSize;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + logs.length, total);

  return (
    <div>
      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Time</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Event Type</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Actor</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Target</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Description</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Result</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {logs.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">{loading ? 'Loading…' : 'No audit entries'}</td></tr>
            )}
            {logs.map(l => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                  {l.event_time ? new Date(l.event_time).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded font-medium ${ACTION_COLORS[l.event_type] || 'bg-gray-100 text-gray-700'}`}>
                    {l.event_type}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">{l.actor_email || '—'}</td>
                <td className="px-3 py-2 text-xs">
                  <span className="text-gray-600">{l.target_type}</span>
                  <span className="font-mono text-gray-400 ml-1">{l.target_id ? `(${l.target_id.slice(0, 8)}…)` : ''}</span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-700 max-w-md truncate" title={l.description}>{l.description}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${l.result === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {l.result || 'success'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
        <div>
          {total > 0 ? (
            <>Showing <span className="font-mono">{showingFrom}</span>–<span className="font-mono">{showingTo}</span> of <span className="font-mono">{total.toLocaleString()}</span></>
          ) : (
            <>{loading ? 'Loading…' : 'No rows'}</>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onPage(Math.max(0, page - 1))} disabled={page === 0 || loading}
                  className="px-2 py-1 border rounded text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center">
            <ChevronLeft className="w-3.5 h-3.5" /> Prev
          </button>
          <span className="px-2">Page {page + 1} / {totalPages.toLocaleString()}</span>
          <button onClick={() => onPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1 || loading}
                  className="px-2 py-1 border rounded text-xs hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center">
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function IntegrityView({ summary, result, onRun }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CheckCard
          label="Duplicates"
          value={summary?.duplicates ?? '—'}
          ok={(summary?.duplicates || 0) === 0}
          hint="Same (site, timestamp, activity_type) appears multiple times"
        />
        <CheckCard
          label="Outliers"
          value={summary?.outliers ?? '—'}
          ok={(summary?.outliers || 0) === 0}
          hint="Values >3σ from the per-activity_type mean"
        />
        <CheckCard
          label="Integrity"
          value={summary?.integrity_percent != null ? `${summary.integrity_percent}%` : '—'}
          ok={(summary?.integrity_percent || 0) >= 95}
          hint="% of native rows with co2e_kg calculated"
        />
      </div>

      {result && (
        <div className={`p-3 border-2 rounded ${result.status === 'ok' ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
          <div className="font-semibold flex items-center gap-2 mb-1">
            <ShieldCheck className={`w-4 h-4 ${result.status === 'ok' ? 'text-emerald-600' : 'text-amber-600'}`} />
            Last integrity check — {result.status}
            <span className="text-xs text-gray-500 ml-auto">{new Date(result.checked_at).toLocaleString()}</span>
          </div>
          <div className="text-sm grid grid-cols-3 gap-2">
            <div>Duplicates: <span className="font-mono">{result.duplicates}</span></div>
            <div>Outliers: <span className="font-mono">{result.outliers}</span></div>
            <div>Orphan tx: <span className="font-mono">{result.orphan_transactions}</span></div>
          </div>
        </div>
      )}

      <button
        onClick={onRun}
        className="px-3 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-medium inline-flex items-center gap-1"
      >
        <PlayCircle className="w-4 h-4" /> Run Integrity Check Now
      </button>
    </div>
  );
}

function CheckCard({ label, value, ok, hint }) {
  return (
    <div className={`p-4 rounded border ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className={`text-xs font-semibold uppercase tracking-wide ${ok ? 'text-emerald-700' : 'text-amber-700'}`}>{label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
      <div className="text-xs text-gray-600 mt-1">{hint}</div>
    </div>
  );
}

function EmptyChart({ hint, compact = false }) {
  return (
    <div className={`flex flex-col items-center justify-center text-center text-gray-400 ${compact ? 'py-3 text-xs' : 'py-10 text-sm'}`}>
      <ActivityIcon className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} text-gray-300 mb-2`} />
      <div>{hint}</div>
    </div>
  );
}
